import { CircleCheck, Lightbulb, Loader2, Sparkles, TriangleAlert } from 'lucide-react'

import type { DailyNutrition, NutritionAdviceSection, NutritionScoreCategory } from '@/api/types/nutrition'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

function ScoreRing({ score }: { score: number }) {
  const radius = 36
  const stroke = 6
  const circumference = 2 * Math.PI * radius
  const progress = (score / 100) * circumference
  const color = score >= 80 ? 'text-emerald-400' : score >= 60 ? 'text-yellow-400' : 'text-red-400'
  const bgColor = score >= 80 ? 'stroke-emerald-400/20' : score >= 60 ? 'stroke-yellow-400/20' : 'stroke-red-400/20'
  const strokeColor = score >= 80 ? 'stroke-emerald-400' : score >= 60 ? 'stroke-yellow-400' : 'stroke-red-400'

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width="88" height="88" className="-rotate-90">
        <circle cx="44" cy="44" r={radius} fill="none" strokeWidth={stroke} className={bgColor} />
        <circle
          cx="44"
          cy="44"
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          className={strokeColor}
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className={`text-xl font-bold tabular-nums ${color}`}>{score}</span>
        <span className="text-[10px] text-muted-foreground">/100</span>
      </div>
    </div>
  )
}

function ScoreBar({ item }: { item: NutritionScoreCategory }) {
  const pct = (item.score / 25) * 100
  const color = pct >= 80 ? 'bg-emerald-400' : pct >= 60 ? 'bg-yellow-400' : 'bg-red-400'
  const textColor = pct >= 80 ? 'text-emerald-400' : pct >= 60 ? 'text-yellow-400' : 'text-red-400'

  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-muted-foreground">{item.category}</span>
        <span className="tabular-nums font-medium">
          {item.score}
          <span className="text-muted-foreground">/25</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[10px] text-muted-foreground">{item.comment}</p>
      {item.improvement && <p className={`text-[10px] ${textColor}`}>→ {item.improvement}</p>}
    </div>
  )
}

const SECTION_CONFIG: Record<NutritionAdviceSection['type'], { icon: typeof CircleCheck; color: string; bg: string }> =
  {
    positive: { icon: CircleCheck, color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
    warning: { icon: TriangleAlert, color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
    suggestion: { icon: Lightbulb, color: 'text-blue-400', bg: 'bg-blue-400/10' },
  }

function AdviceSection({ section }: { section: NutritionAdviceSection }) {
  const config = SECTION_CONFIG[section.type]
  const Icon = config.icon

  return (
    <div className={`rounded-lg p-3 ${config.bg}`}>
      <div className="flex items-start gap-2">
        <Icon className={`size-4 shrink-0 mt-0.5 ${config.color}`} />
        <div className="space-y-0.5 min-w-0">
          <p className="text-xs font-medium">{section.title}</p>
          <p className="text-[11px] leading-relaxed text-muted-foreground">{section.content}</p>
        </div>
      </div>
    </div>
  )
}

type Props = {
  daily: DailyNutrition
  adviceLoading: boolean
  onGenerate: () => void
}

export function AiAdviceCard({ daily, adviceLoading, onGenerate }: Props) {
  const structured = daily.ai_advice_structured
  const hasAdvice = !!structured || !!daily.ai_advice
  const hasMeals = daily.meals.length > 0

  // 未生成時: ボタン1行のみ
  if (!hasAdvice) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="w-full gap-1"
        onClick={onGenerate}
        disabled={adviceLoading || !hasMeals}
      >
        {adviceLoading ? <Loader2 className="size-3 animate-spin" /> : <Sparkles className="size-3" />}
        AIアドバイスを生成
      </Button>
    )
  }

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">AIアドバイス</h3>
        <Button variant="outline" size="sm" onClick={onGenerate} disabled={adviceLoading || !hasMeals}>
          {adviceLoading ? <Loader2 className="mr-1 size-3 animate-spin" /> : <Sparkles className="mr-1 size-3" />}
          再生成
        </Button>
      </div>

      {structured ? (
        <div className="space-y-4">
          {/* スコアサマリー */}
          <div className="flex items-center gap-4">
            <ScoreRing score={structured.total_score} />
            <div className="flex-1 space-y-1.5">
              {structured.scores.map((s) => (
                <ScoreBar key={s.category} item={s} />
              ))}
            </div>
          </div>

          {/* 総評 */}
          <p className="text-xs leading-relaxed border-l-2 border-muted-foreground/30 pl-3">{structured.summary}</p>

          {/* セクション */}
          <div className="space-y-2">
            {structured.sections.map((section, i) => (
              <AdviceSection key={i} section={section} />
            ))}
          </div>

          {/* 改善提案 */}
          {structured.improvements.length > 0 && (
            <div className="rounded-lg border border-blue-400/30 bg-blue-400/5 p-3 space-y-1.5">
              <p className="text-[11px] font-medium text-blue-400">翌日の改善アクション</p>
              <ul className="space-y-1">
                {structured.improvements.map((item, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[11px] leading-relaxed">
                    <span className="text-blue-400 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : daily.ai_advice ? (
        <p className="text-xs leading-relaxed whitespace-pre-wrap">{daily.ai_advice}</p>
      ) : null}
    </Card>
  )
}
